import { useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RestoreIcon from '@mui/icons-material/Restore';
import { ThemeContext } from '../Context';

function RestoreConfirmDialog({
  open,
  onClose,
  onConfirm,
  loading,
  rubricTitle,
  modifiedAt,
  modifiedByName,
  isDeleted = false
}) {
  const { theme } = useContext(ThemeContext);
  const isDarkMode = theme === 'dark';

  // Formater la date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: isDarkMode ? '#1a1a2e' : '#fff'
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <WarningAmberIcon color={isDeleted ? 'success' : 'warning'} sx={{ fontSize: 28 }} />
          <Typography variant="h6" fontWeight="bold">
            {isDeleted ? 'Récupérer la rubrique supprimée' : 'Confirmer la restauration'}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity={isDeleted ? 'info' : 'warning'} sx={{ mb: 2 }}>
          {isDeleted ? (
            <>
              Cette rubrique a été supprimée. En la récupérant, elle sera ajoutée à la fin de votre document.
            </>
          ) : (
            <>
              Cette action remplacera le contenu actuel de la rubrique par la version précédente.
              Le contenu actuel sera sauvegardé dans l'historique avant la restauration.
            </>
          )}
        </Alert>

        <Box
          sx={{
            p: 2,
            bgcolor: isDeleted 
              ? (isDarkMode ? 'rgba(76, 175, 80, 0.1)' : 'rgba(76, 175, 80, 0.05)')
              : (isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'),
            borderRadius: 2
          }}
        >
          <Typography variant="body1" gutterBottom>
            <strong>Rubrique :</strong> {rubricTitle || 'Sans titre'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>{isDeleted ? 'Supprimée le' : 'Version du'} :</strong> {formatDate(modifiedAt)}
          </Typography>
          {modifiedByName && (
            <Typography variant="body2" color="text.secondary">
              <strong>{isDeleted ? 'Supprimée par' : 'Par'} :</strong> {modifiedByName}
            </Typography>
          )}
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {isDeleted 
            ? 'Êtes-vous sûr de vouloir récupérer cette rubrique ?'
            : 'Êtes-vous sûr de vouloir restaurer cette version ?'
          }
        </Typography>
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 1 }}>
        <Button
          onClick={onClose}
          color="inherit"
          disabled={loading}
        >
          Annuler
        </Button>
        <Button
          variant="contained"
          color={isDeleted ? 'success' : 'warning'}
          onClick={onConfirm}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <RestoreIcon />}
        >
          {loading 
            ? (isDeleted ? 'Récupération...' : 'Restauration...')
            : (isDeleted ? 'Récupérer' : 'Restaurer')
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default RestoreConfirmDialog;
